import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PackageStatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Search, Eye, Printer, Users } from "lucide-react";
import { printQRLabels } from "@/lib/printHelper";

export default function DispatcherActivity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/store/dispatcher-processed-packages")
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = !search
    ? rows
    : rows.filter(r =>
        [r.package_id, r.qr_code_id, r.package_name, r.receiver_name, r.last_dispatcher_name]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(search.toLowerCase()))
      );

  return (
    <div data-testid="dispatcher-activity-page" className="space-y-6">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">
          / Dispatcher Processed Packages
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Dispatcher Activity</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
          Packages handled by your store's dispatchers.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="dactivity-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by package, receiver, dispatcher…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${filtered.length} record${filtered.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {filtered.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-20 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <Users className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-lg text-slate-700 dark:text-slate-300 mb-1">No dispatcher activity yet</div>
          <div className="text-sm text-slate-500">When dispatchers process packages, they'll show up here.</div>
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-slate-100/40 dark:bg-slate-900/40">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="bg-slate-100/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="text-left px-5 py-3">QR</th>
                  <th className="text-left px-5 py-3">Package</th>
                  <th className="text-left px-5 py-3">Description</th>
                  <th className="text-left px-5 py-3">Receiver</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Processed By</th>
                  <th className="text-left px-5 py-3">Last Action</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr
                    key={p.id}
                    data-testid={`dactivity-row-${p.package_id}`}
                    className="border-b border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-100/60 hover:dark:bg-slate-900/60 transition-colors"
                  >
                    <td className="px-5 py-3 text-cyan-300 font-mono-tech text-xs">{p.qr_code_id}</td>
                    <td className="px-5 py-3 text-cyan-300 font-mono-tech text-xs">{p.package_id}</td>
                    <td className="px-5 py-3 text-slate-200 max-w-[200px] truncate">{p.package_name}</td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300">
                      <div>{p.receiver_name}</div>
                      <div className="text-xs text-slate-500">{p.receiver_phone}</div>
                    </td>
                    <td className="px-5 py-3"><PackageStatusBadge status={p.status} /></td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{p.last_dispatcher_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
                      {p.last_action_at ? new Date(p.last_action_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          data-testid={`dactivity-view-${p.package_id}`}
                          size="sm" variant="outline"
                          onClick={() => window.location.assign(`/store/packages?focus=${p.package_id}`)}
                          className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button
                          data-testid={`dactivity-print-${p.package_id}`}
                          size="sm" variant="outline"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/store/packages/${p.package_id}`);
                              printQRLabels([{
                                qr_image: data.qr_image, qr_code_id: data.qr_code_id,
                                store_name: data.store_name,
                                assigned_product_name: data.items?.[0]?.item_name,
                              }]);
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                        >
                          <Printer className="w-3 h-3 mr-1" /> Label
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
